import { queryOptions } from "@tanstack/react-query";
import { patientApi } from "./ApiClient";
import { z } from "zod";
import { generateQueryString } from "./Helpers";
import type { IPatientStatus } from "@medoma/models/patients";

export const otpSchema = z.object({
  code: z.string(),
  otpLifetimeInMinutes: z.int(),
});

export const generateOtp = async (patientId: string) => {
  const parsedResponse = await patientApi
    .post(`/patients/${patientId}/care/otp`)
    .json(otpSchema);
  return parsedResponse;
};

const pinSchema = z.object({ pin: z.string() });
export const generateTemporaryPin = async (patientId: string) => {
  const parsedPin = (
    await patientApi
      .post(`/patients/${patientId}/care/pin-temporary`)
      .json(pinSchema)
  ).pin;
  return parsedPin;
};

export const signOutPatient = async (patientId: string) => {
  await patientApi.delete(`/patients/${patientId}/care/session`);
};

const careEpisodeSessionSchema = z.object({
  careEpisodeId: z.string(),
  isSignedInToCare: z.boolean(),
});
const sessionsSchema = z.array(careEpisodeSessionSchema);

export const fetchPatientsSessions = async ({
  careEpisodeIds,
  statuses,
}: {
  careEpisodeIds?: string[];
  statuses?: IPatientStatus[];
}) => {
  const queryString =
    careEpisodeIds && careEpisodeIds.length > 100
      ? // Avoid ~8000 character URL limit (same issue as MED-6269 for fetchPatients).
        // Fetching all sessions is acceptable here since the caller filters by the returned IDs.
        generateQueryString({ status: statuses })
      : generateQueryString({ careEpisodeIds, status: statuses });
  const parsedPatientSessions = await patientApi
    .get(`/patients/care/session${queryString}`)
    .json(sessionsSchema);
  return parsedPatientSessions;
};

const fetchPatientSession = async (careEpisodeId: string) => {
  const sessions = await patientApi
    .get(
      `/patients/care/session${generateQueryString({ careEpisodeIds: [careEpisodeId] })}`,
    )
    .json(sessionsSchema);
  return careEpisodeSessionSchema.parse(sessions[0]);
};

const TEN_SECONDS = 10 * 1000;

export const patientSessionQueryOptions = (patientId: string) =>
  queryOptions({
    queryKey: ["patient-session", patientId],
    queryFn: async () => {
      const session = await fetchPatientSession(patientId);
      return session.isSignedInToCare;
    },
    refetchInterval: TEN_SECONDS,
  });
